import { PageRef } from 'providers/page-ref';
import { Nutrients, FoodMeasure } from '@cf-shared/data-model/food';
import { ErrandAnswerShape } from 'pages/foods/add-food-errand';

/** How the food details screen was closed, when it was not a plain Back or a save (#5520). */
export enum FoodDetailsExit {
  // the user pressed Log, and the page underneath finishes the errand with this food
  Log = 'log',
}

/** What the details screen hands back when opened with `logReturnsToCaller`. */
export type FoodDetailsResult = { exit: FoodDetailsExit; foodId: string };

export type EditFoodPageParams = {
  id?: string;
  name?: string;
  ean?: string;
  mode?: 'createFood' | 'createRecipe' | 'view';
  // The add-food errand this editor finishes when it saves a food (#5377) -- see
  // `pages/foods/add-food-errand.ts`. Without `errandAnswerShape` a waiting page gets the bare id.
  returnCallback?: string;
  errandAnswerShape?: ErrandAnswerShape;
  gotoLogFoodAfterSelect_dayDate?: string;
  // Log on the details screen closes it with a `FoodDetailsResult` for the page underneath to act
  // on, instead of logging to today on top of it (#5520). Set by pages that are adding a food.
  logReturnsToCaller?: boolean;
  local?: {
    returnContext?: unknown;
    nutrients?: Nutrients;         // pre-fill from label scan
    measures?: FoodMeasure[];      // pre-fill from label scan
    labelScanConfidence?: number;  // 0-1 confidence from label scan
    labelScanNutrientConfidences?: { [key: string]: number }; // per-nutrient confidence 0-1
    labelScanJsonRepaired?: boolean; // true if LLM response was truncated and patched
    foodName?: string;             // extracted product name from label scan
    brandName?: string;            // extracted brand name from label scan
    ingredients?: string;          // extracted ingredients text from label scan
    description?: string;          // extracted description from label scan
    labelStyle?: 'US' | 'EU';      // detected label regulatory style from label scan
    barcode?: string;              // EAN/UPC barcode digits extracted from label scan
    /**
     * The barcode the USER scanned before starting the label scan, because scanning it found
     * no food (#4968). Distinct from `barcode` above, which is whatever the label scan read
     * off the package itself — the two come from different places and can disagree, which is
     * why they are kept apart rather than one overwriting the other.
     */
    scannedBarcode?: string;
  };
};

export const IEditFoodPage: PageRef<EditFoodPageParams, void> = {
  pageName: 'EditFoodPage',
} as PageRef<EditFoodPageParams, void>;

// in-memory transfer for label scan image (too large for sessionStorage nav params)
export let pendingLabelScanImageDataUrl: string | null = null;
export function setPendingLabelScanImage(dataUrl: string | null) {
  pendingLabelScanImageDataUrl = dataUrl;
}
